import type { ReactNode } from 'react';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type SxProps,
} from '@mui/material';
import { EmptyState } from './EmptyState';

export interface DataTableColumn<Row> {
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  nowrap?: boolean;
  hidden?: boolean;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string | number;
  /** Yükleniyor → başlıkla birlikte iskelet satırlar. */
  loading?: boolean;
  loadingRows?: number;
  /** `!loading && rows.length === 0` iken gösterilir. */
  emptyState?: ReactNode;
  onRowClick?: (row: Row) => void;
  /** Sağa hizalı, satır-başına ek aksiyon hücresi. */
  rowActions?: (row: Row) => ReactNode;
  size?: 'small' | 'medium';
  stickyHeader?: boolean;
  maxHeight?: number | string;
  getRowSx?: (row: Row) => SxProps;
}

/** Düz MUI `<Table>` üstüne ince liste tablosu (x-data-grid YASAK — TS 4.9.5). Client-side,
 *  pagination yok — mevcut kod tabanıyla aynı. */
export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  loading = false,
  loadingRows = 6,
  emptyState,
  onRowClick,
  rowActions,
  size = 'small',
  stickyHeader,
  maxHeight,
  getRowSx,
}: DataTableProps<Row>) {
  const visibleColumns = columns.filter((c) => !c.hidden);
  const totalCols = visibleColumns.length + (rowActions ? 1 : 0);

  return (
    <TableContainer component={Paper} variant="outlined" sx={maxHeight ? { maxHeight } : undefined}>
      <Table size={size} stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {visibleColumns.map((col) => (
              <TableCell key={col.id} align={col.align} sx={{ width: col.width }}>
                {col.header}
              </TableCell>
            ))}
            {rowActions && <TableCell align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: loadingRows }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: totalCols }).map((__, j) => (
                  <TableCell key={j}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!loading && rows.length === 0 && (
            <>{emptyState ?? <EmptyState variant="row" colSpan={totalCols} title="Kayıt bulunamadı." />}</>
          )}

          {!loading &&
            rows.map((row) => (
              <TableRow
                key={getRowKey(row)}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ ...(onRowClick ? { cursor: 'pointer' } : {}), ...(getRowSx?.(row) ?? {}) }}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={col.nowrap ? { whiteSpace: 'nowrap' } : undefined}
                  >
                    {col.cell(row)}
                  </TableCell>
                ))}
                {rowActions && (
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {rowActions(row)}
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
